import { ErrorChip, FormGroup, Input } from '@starsoft/common/components';
import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { KYCStatus } from '@starsoft/common/enums';
import {
  useDebounce,
  useKycMeByOriginIdWithOrigin,
  usePhoneNumberForm,
  useTranslation,
} from '@starsoft/common/hooks';
import { UserPhoneNumberFormProps } from '@starsoft/common/models';
import { ChangeEvent } from 'react';
import { useFormContext } from 'react-hook-form';

/**
 * PhoneNumberInput Component
 * This component renders an input field for entering a phone number, with additional functionality
 * for error handling, debouncing, and conditional rendering based on KYC status.
 *
 * @param {Object} props - The properties for the component.
 * @param {boolean} [props.isContainerColor] - Optional flag to determine if the container should have a color.
 * @param {boolean} [props.isKycFlow] - Optional flag to indicate if the form is part of the KYC (Know Your Customer) flow.
 *
 * @returns {JSX.Element} The rendered phone number input component.
 *
 * @example
 * // Example usage:
 * <PhoneNumberInput isContainerColor={true} isKycFlow={false} />
 */
export default function PhoneNumberInput({
  isContainerColor,
  isKycFlow,
}: {
  isContainerColor?: boolean;
  isKycFlow?: boolean;
}) {
  // Hook for translation in the 'account' namespace
  const { t } = useTranslation('account');

  // Access form context to manage form state
  const {
    watch,
    register,
    setValue,
    formState: { touchedFields },
  } = useFormContext<UserPhoneNumberFormProps & CreateDepositRequestDto>();

  // Custom hook to manage phone number form state and actions
  const {
    countriesQuery: { error, refetch, isLoading },
    isErrorPhoneNumberLookUp,
  } = usePhoneNumberForm({
    watch,
    setValue,
  });

  // Watch for changes in the 'phoneNumber' field
  const phoneNumber: string | undefined = watch('phoneNumber');
  // Debounce the phone number input to reduce the frequency of updates
  const debouncedPhoneNumber: string | undefined = useDebounce(phoneNumber);

  // Hook to get KYC status based on origin ID
  const { kyc } = useKycMeByOriginIdWithOrigin({
    enabled: isKycFlow,
  });

  // Determine if the phone number is verified based on KYC status
  const isVerified: boolean = kyc?.status === KYCStatus.Verified && isKycFlow;

  /**
   * onChange Function
   * This function updates the input value by removing any non-digit characters.
   *
   * @param {ChangeEvent<HTMLInputElement>} e - The change event from the input field.
   *
   * @example
   * // Example usage:
   * onChange({ target: { value: '123abc' } }); // Input value becomes '123'
   */
  function onChange(e: ChangeEvent<HTMLInputElement>) {
    e.target.value = e.target.value.replace(/\D/g, '');
  }

  // Render the form group with input and error handling
  return (
    <FormGroup label={t('user_phone_number_label')}>
      {error ? (
        <ErrorChip action={refetch} cardBg />
      ) : (
        <Input
          hideLock
          name="phoneNumber"
          readOnly={isVerified}
          register={register}
          registerOptions={{
            required: true,
            onChange: onChange,
          }}
          isContainerColor={isContainerColor}
          showSpinner={isLoading}
          placeholder={t('user_phone_number_label')}
          isError={isErrorPhoneNumberLookUp && touchedFields.phoneNumber}
          errorMessage={
            debouncedPhoneNumber?.length < 3 ||
            debouncedPhoneNumber?.length > 21
              ? t('invalid_phone_number_length')
              : t('invalid_phone_number')
          }
        />
      )}
    </FormGroup>
  );
}
